import Card from '@albertsons/uds/molecule/Card';
import Button from '@albertsons/uds/molecule/Button';
import './FillTableCard.css';
import React, { useState, useEffect, useRef } from 'react';
import ExcelTable from './ExcelTable';
import { useToast } from '../layout/Layout';
import { useTableHandlers } from './useTableHandlers';
import Papa from 'papaparse';
import { v4 as uuidv4 } from 'uuid';
import Tooltip from '@albertsons/uds/molecule/Tooltip';
import Environment from '../utils/Environment';

interface Column {
    key: string;
    name: string;
}

interface FillTableCardProps {
    selectedDC: string;
    tabType?: string;
    files: File[];
    filesFillTable: File[];
    setFilesFillTable: React.Dispatch<React.SetStateAction<File[]>>;
    isSecondCardOpen: boolean;
    setSecondCardOpen: React.Dispatch<React.SetStateAction<boolean>>;
    setOpen: (isOpen: boolean) => void;
    formatFileSize: (size: number) => string;
    fillTablePopulateButton: string;
    columns: Column[];
    labelDateKey?: string | null;
    isUploading: boolean;
    setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
    fillTableCardOpen: boolean;
    setFillCardOpen: React.Dispatch<React.SetStateAction<boolean>>;
    userId?: string;
}

type Row = Record<string, string>;

const FillTableCard: React.FC<FillTableCardProps> = ({
    selectedDC,
    tabType,
    files,
    filesFillTable,
    setFilesFillTable,
    setSecondCardOpen,
    formatFileSize,
    fillTablePopulateButton,
    columns,
    labelDateKey,
    isUploading,
    setIsUploading,
    fillTableCardOpen,
    setFillCardOpen,
    userId
}) => {
    const { showError, showWarning, clearToast } = useToast();
    
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [filesValidated, setFilesValidated] = useState<File[]>([]);
    const [filesNotValidated, setFilesNotValidated] = useState<File[]>([]);
    const [updatedFilesWithStatus, setUpdatedFilesWithStatus] = useState<{ name: string; status: string }[]>([]);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const [isFileValidated, setIsFileValidated] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [visibleCount] = useState(25);
    
    // Local alert state for ExcelTable component
    const [alertMessageExcelTable, setAlertMessageExcelTable] = useState<string | null>(null);
    const [alertSeverityExcelTable, setAlertSeverityExcelTable] = useState<'success' | 'error' | 'warning' | 'info'>('info');
    
    // Wrapper functions - ExcelTable now handles toasts directly, so these are just pass-through
    const handleSetAlertMessage = (message: string | null) => {
        setAlertMessageExcelTable(message);
        // ExcelTable now handles toasts directly, no need to duplicate here
    };
    
    const handleSetAlertSeverity = (severity: 'success' | 'error' | 'warning' | 'info') => {
        setAlertSeverityExcelTable(severity);
    };
    
    const handleCloseSnackbarExcelTable = () => {
        setAlertMessageExcelTable(null);
    };
    
    const {
        rows,
        setRows,
        editingCell,
        setEditingCell,
        selectedRows,
        setSelectedRows,
        tableRef,
        handleCellChange,
        handleClick,
        startResize,
        handlePaste,
        toggleRowSelection,
        addRow,
        deleteSelected,
        handleCopySelectedRows,
        bulkClearCells,
        handleUndo,
        handleRedo,
        undoStack,
        redoStack,
        resetHistory,
        jumpToPageError,
        setJumpToPageError,
        paginationModel,
        setPaginationModel,
        handlePageSizeChange,
        handleJumpToPage,
        currentPage,
        createPageRange,
        validationErrors,
        setValidationErrors
    } = useTableHandlers(columns, tabType, isPopupOpen, selectedDC);

    const [jumpToPageValue, setJumpToPageValue] = React.useState('');

    const parseCSVFile = (file: File): Promise<{ rows: any[]; columns: any[] }> => {
        return new Promise((resolve, reject) => {
            Papa.parse(file, {
                header: true,
                skipEmptyLines: true,
                complete: (result) => {
                    const parsedRows = result.data.map((row: any, rowIndex: number) => {
                        const mappedRow: any = { tempKey: uuidv4() };
                        columns.forEach(col => {
                            let value = row[col.key] || '';
                            // Clean single quotes from Shipment ID values
                            if (col.key === 'Shipment ID' && typeof value === 'string') {
                                value = value.replace(/'/g, '');
                            }
                            // Convert Facility values to uppercase for IN02 tab
                            if (tabType === 'IN02' && col.key === 'Facility' && typeof value === 'string') {
                                value = value.toUpperCase();
                            }
                            mappedRow[col.key] = value;
                        });
                        return mappedRow;
                    });
                    
                    // Validate Load Group fields for IN02 tab when CSV is loaded
                    if (tabType === 'IN02') {
                        const newValidationErrors: Record<string, boolean> = {};
                        const loadGroupFields = ['Load Group FRZ', 'Load Group GRO', 'Load Group PER', 'Load Group GML', 'Load Group PRO'];
                        
                        // Check if this is a Custom IN02 DC
                        const customIN02DCs = Environment.getCustomIN02DCList();
                        const isCustomIN02DC = customIN02DCs.includes(selectedDC);

                        // Check if this is a Combined IN02 DC (e.g. WAUB)
                        const combinedIN02DCs = Environment.getCombinedIN02DCList();
                        const isCombinedIN02DC = combinedIN02DCs.includes(selectedDC);
                        
                        const isValidLoadGroupValue = (value: any): boolean => {
                            if (!value && value !== 0) return true; // Empty values are allowed
                            
                            const stringValue = String(value).trim();
                            if (stringValue === '') return true; // Empty string is allowed
                            
                            if (isCustomIN02DC) {
                                // Custom IN02: Allow any numerical value > 0
                                const digitsOnly = /^[0-9]+$/.test(stringValue);
                                if (!digitsOnly) {
                                    return false;
                                }
                                const numValue = parseInt(stringValue, 10);
                                return numValue > 0;
                            } else if (isCombinedIN02DC) {
                                // Combined IN02 (e.g. WAUB): Allow any numerical value > 0
                                const digitsOnly = /^[0-9]+$/.test(stringValue);
                                if (!digitsOnly) {
                                    return false;
                                }
                                const numValue = parseInt(stringValue, 10);
                                return numValue > 0;
                            } else {
                                // Normal IN02: Must be 1 or 2 digits (1-99)
                                const oneOrTwoDigits = /^\d{1,2}$/.test(stringValue);
                                if (!oneOrTwoDigits) {
                                    return false;
                                }
            
                                // Parse and validate range: must be between 1-99
                                const numValue = parseInt(stringValue, 10);
                                return numValue > 0 && numValue <= 99;
                            }
                        };
                        
                        parsedRows.forEach((row, rowIndex) => {
                            loadGroupFields.forEach(field => {
                                const value = row[field];
                                if (value && value.toString().trim() !== '' && !isValidLoadGroupValue(value)) {
                                    const cellKey = `${rowIndex}-${field}`;
                                    newValidationErrors[cellKey] = true;
                                }
                            });
                        });
                        setValidationErrors(newValidationErrors);
                    }
                    
                    const columnsArray = columns.map(col => ({ key: col.key, name: col.name }));
                    resolve({ rows: parsedRows, columns: columnsArray });
                },
                error: (error) => {
                    showError(`Error parsing CSV file: ${error.message}`);
                    reject(error);
                },
            });
        });
    };
    useEffect(() => {
        if (filesFillTable.length > 0) {
            if (isFileValidated) {
                setFilesValidated(filesFillTable);
                setFilesNotValidated([]);
            } else {
                setFilesValidated([]);
                setFilesNotValidated(filesFillTable);
            }
        } else {
            setFilesValidated([]);
            setFilesNotValidated([]);
        }
    }, [filesFillTable, isFileValidated]);

    useEffect(() => {
        if (filesValidated.length > 0 || filesNotValidated.length > 0) {
            const newFilesWithStatus: { name: string; status: string }[] = [];
            filesValidated.forEach((file) => {
                newFilesWithStatus.push({
                    name: `${file.name} - Size - ${formatFileSize(file.size)} - Status - `,
                    status: 'Validated',
                });
            });
            filesNotValidated.forEach((file) => {
                newFilesWithStatus.push({
                    name: `${file.name} - Size - ${formatFileSize(file.size)} - Status - `,
                    status: 'Not Validated',
                });
            });
            setUpdatedFilesWithStatus(newFilesWithStatus);
        } else {
            setUpdatedFilesWithStatus([]);
        }
    }, [filesValidated, filesNotValidated, formatFileSize]);

    const handleCreateTableClick = () => {
        clearToast();
        handleReset();
        resetHistory();
        setIsPopupOpen(true);
    };

    const handleClosePopup = () => {
        handleReset();
        resetHistory();
        setIsPopupOpen(false);
    };
    const [highlightMap, setHighlightMap] = useState<Record<string, boolean>>({});
    const filteredRows = rows.slice(0, visibleCount);
    const handleReset = () => {
        setFilesFillTable([]);
        setSecondCardOpen(false);
        setFilesNotValidated([]);
        setFilesValidated([]);
        setSelectedRows(new Set());
        updatedFilesWithStatus.length = 0;
        setIsFileValidated(false);
        // Always reset to 25 empty rows
        const filledRows: Row[] = [];
        while (filledRows.length < 25) {
            const emptyRow: Row = {};
            columns.forEach(col => {
                emptyRow[col.key] = '';
            });
            filledRows.push(emptyRow);
        }
        setRows(filledRows);
        resetHistory();
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        setPaginationModel({ page: 0, pageSize: 25 });
        setHighlightMap({});
        setJumpToPageError(null);
        setValidationErrors({});
        setAlertMessageExcelTable(null);
        setAlertSeverityExcelTable('success');
    };

    return (
        <>
            <Card collapsible title="Fill Table" isOpen={fillTableCardOpen} onChange={setFillCardOpen} className="min-w-[600px]">
                <Card.Content>
                    <div className="minpadding">
                        <div
                            style={{
                                alignItems: 'center',
                                display: 'flex',
                                paddingTop: '16px',
                                position: 'relative',
                            }}
                        >
                            <p style={{ fontSize: '12px', fontWeight: 'bold', marginRight: '8px' }}>
                                Please create your own table by editing and filling all the relevant fields
                            </p>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                width={120}
                                disabled={selectedDC.length === 0}
                                onClick={() => {
                                    if (files.length > 0) {
                                        showError('Please finish the file upload before proceeding with the Fill Table operation');
                                    } else {
                                        handleCreateTableClick();
                                    }
                                }
                                }
                            >
                                Create Table
                            </Button>
                        </div>
                    </div>
                </Card.Content>
            </Card>

            {isPopupOpen && (
                <div className="popup-overlay">
                    <Card title="Fill Table" isOpen={true} className="popup-card">
                        <Card.Header>
                            <div style={{ alignContent: 'center', paddingLeft: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    {updatedFilesWithStatus.length > 0 ? (
                                        <div className='dropDownContainer' style={{ flex: 1 }}>
                                            <select style={{ border: 'none', outline: 'none', fontWeight: 'normal', width: '100%' }}>
                                                {updatedFilesWithStatus.map((file, idx) => {
                                                    const fileName = file.name.split(' - Size - ')[0];
                                                    const displayName = fileName.length > 30 ? fileName.slice(0, 27) + '...' : fileName;
                                                    return (
                                                        <option
                                                            key={idx}
                                                            value={file.name}
                                                            style={{
                                                                color: file.status === 'Validated' ? 'green' : 'red',
                                                                backgroundColor: 'white',
                                                            }}
                                                            title={fileName.length > 30 ? fileName : undefined}
                                                        >
                                                            {fileName.length > 30 ? (
                                                                <Tooltip zIndex={10} anchor='right' label={fileName}>
                                                                    <span className='m-2 whitespace-nowrap' style={{ fontSize: '12px' }}>{displayName} - Size - {file.name.split(' - Size - ')[1]} {file.status}</span>
                                                                </Tooltip>
                                                            ) : (
                                                                <span style={{ fontSize: '12px' }}>{fileName} - Size - {file.name.split(' - Size - ')[1]} {file.status}</span>
                                                            )}
                                                        </option>
                                                    );
                                                })}
                                            </select>
                                        </div>
                                    ) : (
                                        <p className="no-files-message" style={{ fontSize: '12px', marginRight: '8px' }}>No files selected yet</p>
                                    )}
                                    <span style={{ fontWeight: 'bold', marginLeft: 'auto', fontSize: '12px', whiteSpace: 'nowrap' }}>Selected DC : {selectedDC}</span>
                                </div>
                            </div>
                            <div style={{ marginLeft: 'auto' }}>
                                <Button
                                    width={300}
                                    disabled={selectedDC.length === 0 || filesFillTable.length >= 1}
                                    onClick={() => {
                                        document.getElementById('fileInput')?.click();
                                    }}
                                >
                                    {fillTablePopulateButton}
                                </Button>
                                <input
                                    id="fileInput"
                                    type="file"
                                    accept=".csv,text/csv,application/csv"
                                    style={{ display: 'none' }}
                                    onChange={async (event) => {
                                        if (event.target.files) {
                                            const filesArray = Array.from(event.target.files);
                                            
                                            // Validate CSV files
                                            const csvFiles = filesArray.filter((file) => {
                                                const isCSVType = file.type === "text/csv" || file.type === "application/csv" || file.type === "";
                                                const isCSVExtension = file.name.toLowerCase().endsWith(".csv");
                                                return isCSVType && isCSVExtension;
                                            });

                                            if (csvFiles.length === 0) {
                                                const fileTypes = filesArray.map(f => f.name.split('.').pop()?.toUpperCase() || 'unknown').join(', ');
                                                showError(`Unsupported file format(s): ${fileTypes}. Only CSV files are allowed for Fill Table.`);
                                                if (event.target) event.target.value = ""; // Clear the input value
                                                return;
                                            }

                                            // Check if some files were filtered out
                                            if (csvFiles.length < filesArray.length) {
                                                const rejectedCount = filesArray.length - csvFiles.length;
                                                const rejectedFiles = filesArray.filter(file => !csvFiles.includes(file));
                                                const rejectedTypes = rejectedFiles.map(f => f.name.split('.').pop()?.toUpperCase() || 'unknown').join(', ');
                                                
                                                showWarning(`${rejectedCount} file(s) rejected (${rejectedTypes} format). Only CSV files are accepted. ${csvFiles.length} CSV file(s) will be processed.`);
                                            }

                                            setFilesFillTable((prevFiles) => [...prevFiles, ...csvFiles]);
                                            const results = await Promise.all(csvFiles.map(file => parseCSVFile(file)));
                                            let newRows = results.flatMap(result => result.rows);

                                            // Check row count for IN02 - limit to 1000 rows
                                            if ((tabType || 'XDOCK') === 'IN02' && newRows.length > 1000) {
                                                showError(`The file contains ${newRows.length} rows, which exceeds the 1000 row limit for Fill Table. Please use the Upload feature for large File.`);
                                                setFilesFillTable([]); // Clear the files
                                                return;
                                            }

                                            // Remove blank lines for IN02: if only tempKey is present and all other columns are empty, remove
                                            if ((tabType || 'XDOCK') === 'IN02') {
                                                const dataKeys = columns.map(col => col.key);
                                                newRows = newRows.filter(row =>
                                                    dataKeys.some(key => typeof row[key] === 'string' && row[key].trim() !== '')
                                                );
                                            }

                                            // General validation: Check if any rows have data in expected columns
                                            const hasValidData = newRows.some(row =>
                                                columns.some(col => typeof row[col.key] === 'string' && row[col.key].trim() !== '')
                                            );
                                            if (!hasValidData) {
                                                showError(`Uploaded file does not match with ${tabType || 'XDOCK'} template defined`);
                                                setFilesFillTable([]); // Clear the files
                                                return;
                                            }

                                            // Create a function to generate an empty row using the latest columns
                                            const createEmptyRow = () => {
                                                const emptyRow: Record<string, string> = {};
                                                columns.forEach(col => {
                                                    emptyRow[col.key] = '';
                                                });
                                                return emptyRow;
                                            };

                                            setRows(prevRows => {
                                                // Keep only rows that have at least one non-empty value
                                                const nonEmptyRows = prevRows.filter(row =>
                                                    Object.values(row).some(val => typeof val === 'string' && val.trim() !== '')
                                                );
                                                let filledRows = [...nonEmptyRows, ...newRows];
                                                // Fill up to 25 rows with empty rows if needed
                                                while (filledRows.length < 25) {
                                                    filledRows.push(createEmptyRow());
                                                }
                                                return filledRows;
                                            });
                                        }
                                        event.target.value = '';
                                    }}
                                />
                            </div>
                        </Card.Header>
                        <Card.Content>
                            <div>
                                <ExcelTable
                                    columns={
                                        (columns?.map((col) =>
                                            typeof col === 'string'
                                                ? { key: col, name: col }
                                                : { key: (col as any).key, name: (col as any).label ?? (col as any).name }
                                        ) ?? [])
                                    }
                                    handleClosePopup={handleClosePopup}
                                    handleReset={handleReset}
                                    selectedDC={selectedDC}
                                    tabType={tabType || 'XDOCK'}
                                    filesFillTable={filesFillTable}
                                    setFilesFillTable={setFilesFillTable}
                                    labelDateKey={labelDateKey ?? null}
                                    isLoading={isLoading}
                                    isUploading={isUploading}
                                    isFileValidated={isFileValidated}
                                    rows={rows}
                                    setRows={setRows}
                                    filteredRows={filteredRows}
                                    setAlertMessageFillTable={handleSetAlertMessage}
                                    setAlertSeverityFillTable={handleSetAlertSeverity}
                                    alertMessageFillTable={alertMessageExcelTable}
                                    alertSeverityFillTable={alertSeverityExcelTable}
                                    setIsFileValidated={setIsFileValidated}
                                    setIsLoading={setIsLoading}
                                    setIsUploading={setIsUploading}
                                    handleCloseSnackbarFillTable={handleCloseSnackbarExcelTable}
                                    tableRef={tableRef}
                                    handlePaste={handlePaste}
                                    startResize={startResize}
                                    selectedRows={selectedRows}
                                    setSelectedRows={setSelectedRows}
                                    toggleRowSelection={toggleRowSelection}
                                    handleCellChange={handleCellChange}
                                    handleCopySelectedRows={handleCopySelectedRows}
                                    bulkClearCells={bulkClearCells}
                                    handleClick={handleClick}
                                    editingCell={editingCell}
                                    highlightMap={highlightMap}
                                    setHighlightMap={setHighlightMap}
                                    setEditingCell={setEditingCell}
                                    deleteSelected={deleteSelected}
                                    addRow={addRow}
                                    handleUndo={handleUndo}
                                    handleRedo={handleRedo}
                                    undoStack={undoStack}
                                    redoStack={redoStack}
                                    handleJumpToPage={handleJumpToPage}
                                    jumpToPageError={jumpToPageError}
                                    setJumpToPageError={setJumpToPageError}
                                    paginationModel={paginationModel}
                                    setPaginationModel={setPaginationModel}
                                    handlePageSizeChange={handlePageSizeChange}
                                    jumpToPageValue={jumpToPageValue}
                                    setJumpToPageValue={setJumpToPageValue}
                                    currentPage={currentPage}
                                    createPageRange={createPageRange}
                                    validationErrors={validationErrors}
                                    setValidationErrors={setValidationErrors}
                                    userId={userId}
                                />
                            </div>
                        </Card.Content>
                    </Card>
                </div>
            )}
        </>
    );
};
export default FillTableCard;
